import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as rewardActions from '../../../../../../redux/rewards/actions';
import * as birthdayCampaignActions from '../../../../../../redux/birthdayCampaigns/actions';
import { BirthdayCampaign as BirthdayCampaignModel } from '../../../../../../redux/birthdayCampaigns/models';
import { makeGetReward } from '../../../../../../redux/rewards/selectors';
import {
  getIsFetching,
  getProgramBirthdayCampaign,
} from '../../../../../../redux/birthdayCampaigns/selectors';

import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import CreateIcon from '@material-ui/icons/Create';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Typography from '@material-ui/core/Typography';

import NewBirthdayCampaignDialog from './NewBirthdayCampaignDialog';

//------------------------------------------------------------------------------
// Styles
//------------------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  subheader: {
    color: theme.palette.common.black,
  },
  listItem: {
    minWidth: theme.spacing(4),
  },
  root: {
    marginTop: 10,
  },
}));

const useBlankSlateStyles = makeStyles((theme) => ({
  root: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wrapper: {
    textAlign: 'center',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
}));

const useCampaignStyles = makeStyles((theme) => ({
  subheader: {
    color: theme.palette.common.black,
  },
  reward: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
}));

//------------------------------------------------------------------------------
// Types
//------------------------------------------------------------------------------

type Props = {
  programId: string;
  merchantId: string;
};

//------------------------------------------------------------------------------
// Component
//------------------------------------------------------------------------------

function BlankSlate({ onAction }: { onAction: () => void }) {
  const classes = useBlankSlateStyles();

  return (
    <div className={classes.wrapper}>
      <Button variant="contained" color="primary" onClick={onAction}>
        Get Started
      </Button>
    </div>
  );
}

function Campaign({ campaign }: { campaign: BirthdayCampaignModel }) {
  const dispatch = useDispatch();
  const classes = useCampaignStyles();

  const reward = useSelector(makeGetReward(campaign.rewardId));

  useEffect(() => {
    dispatch(rewardActions.getReward(campaign.rewardId));
  }, [campaign.rewardId, dispatch]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.reward}>
      <Avatar
        variant="rounded"
        src={reward.image ? reward.image.versions['xl'] : undefined}
        className={classes.avatar}
      />
      <Typography>{reward.title}</Typography>
    </div>
  );
}

export default function BirthdayCampaign({ merchantId, programId }: Props) {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const isFetching = useSelector(getIsFetching);
  const campaign = useSelector(getProgramBirthdayCampaign(programId));

  // Close the create dialog
  const handleCloseCreateDialog = () => setCreateDialogOpen(false);

  // Closes the menu
  const handleCloseMenu = () => setAnchorEl(null);

  // Opens the menu
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    dispatch(
      birthdayCampaignActions.getLoyaltyProgramBirthdayCampaign(programId)
    );
  }, [dispatch, programId]);

  return (
    <div className={classes.root}>
      <Card>
        <CardHeader
          action={
            <div>
              <IconButton onClick={handleOpenMenu}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => setCreateDialogOpen(true)}>
                  <ListItemIcon className={classes.listItem}>
                    <CreateIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography>New Campaign</Typography>
                </MenuItem>
              </Menu>
            </div>
          }
          subheader="Birthday Campaign"
          classes={{
            subheader: classes.subheader,
          }}
        />

        <CardContent>
          {(() => {
            if (isFetching) {
              return <CircularProgress size={30} />;
            } else {
              return !!campaign ? (
                <Campaign campaign={campaign} />
              ) : (
                <BlankSlate onAction={() => setCreateDialogOpen(true)} />
              );
            }
          })()}
        </CardContent>
      </Card>

      <NewBirthdayCampaignDialog
        merchantId={merchantId}
        programId={programId}
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        onExit={handleCloseMenu}
      />
    </div>
  );
}
